// src/components/TimekeepingScanner.jsx - Versione con gestione posizione
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import timekeepingService from '../services/timekeepingService';
import Notification from './Notification';
import LocationSelectionModal from './LocationSelectionModal';
import { getKioskDeviceLocation, getLocationName } from '../utils/locationUtils';

/**
 * TimekeepingScanner con sistema di notifiche migliorato e gestione posizione
 */
const TimekeepingScanner = ({ isAdmin = false, deviceId = '', kioskMode = false }) => {
  // Stati principali
  const [scanType, setScanType] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [isCaptured, setIsCaptured] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '', size: 'normal' });
  
  // ✅ NUOVI STATI per gestione posizione (solo admin mode)
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [pendingTimbratura, setPendingTimbratura] = useState(null);
  
  // Refs
  const html5QrCode = useRef(null);
  const timeoutRef = useRef(null);

  // Costanti
  const SCAN_TIMEOUT = 30000;

  // Funzione per mostrare notifiche migliorate
  const showNotification = useCallback((message, type = 'info', customSize = null) => {
    // Determina la dimensione in base al tipo e al contesto
    let size = customSize || 'normal';
    
    if (kioskMode) {
      // In modalità kiosk, usa dimensioni più grandi per visibilità
      size = (type === 'success' || type === 'error') ? 'large' : 'normal';
    } else if (isAdmin) {
      // In modalità admin, dimensioni normali ma grandi per timbrature
      size = (type === 'success' || type === 'error') ? 'large' : 'normal';
    }

    setNotification({
      show: true,
      message,
      type,
      size
    });
  }, [kioskMode, isAdmin]);

  // Funzione per chiudere notifiche
  const closeNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, show: false }));
  }, []);

  // Inizializza le camere disponibili
  const initializeCameras = useCallback(async () => {
    try {
      console.log('Inizializzazione camere...');
      const devices = await Html5Qrcode.getCameras();
      console.log('Camere trovate:', devices);
      
      if (devices && devices.length > 0) {
        setCameras(devices);
        
        if (kioskMode) {
          // In modalità kiosk, preferisci la camera posteriore
          const backCamera = devices.find(device =>
            device.label.toLowerCase().includes('back') ||
            device.label.toLowerCase().includes('rear') ||
            device.label.toLowerCase().includes('environment') ||
            device.label.toLowerCase().includes('facing back') ||
            device.label.toLowerCase().includes('world')
          );
          
          const selectedCameraId = backCamera ? backCamera.id : devices[devices.length - 1].id;
          setSelectedCamera(selectedCameraId);
          
          console.log('Camera selezionata (kiosk - posteriore):', {
            selectedId: selectedCameraId,
            backCameraFound: !!backCamera,
            selectedLabel: backCamera ? backCamera.label : devices[devices.length - 1].label
          });
        } else {
          setSelectedCamera(devices[0].id);
          console.log('Camera selezionata (admin):', devices[0].id);
        }
      } else {
        console.warn('Nessuna camera trovata');
        showNotification(
          '📷 Nessuna camera rilevata sul dispositivo. Verifica i permessi e ricarica la pagina.',
          'error',
          'large'
        );
      }
    } catch (error) {
      console.error('Errore accesso camera:', error);
      showNotification(
        `🚫 Errore accesso alla camera: ${error.message}. Verifica i permessi e riprova.`,
        'error',
        'large'
      );
    }
  }, [kioskMode, showNotification]);

  // Inizializza al montaggio del componente
  useEffect(() => {
    initializeCameras();
    
    return () => {
      stopScanner();
    };
  }, [initializeCameras]);

  // Ferma lo scanner e pulisce le risorse
  const stopScanner = useCallback(async () => {
    try {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (html5QrCode.current) {
        try {
          const state = html5QrCode.current.getState();
          if (state === Html5Qrcode.SCANNING || state === Html5Qrcode.PAUSED) {
            await html5QrCode.current.stop();
          }
        } catch (stopError) {
          console.warn('Errore stop scanner (non critico):', stopError);
        }
        html5QrCode.current = null;
      }

      setIsScanning(false);
      setIsCaptured(false);
    } catch (error) {
      console.error('Errore stop scanner:', error);
      html5QrCode.current = null;
      setIsScanning(false);
      setIsCaptured(false);
    }
  }, []);

  // ✅ NUOVA FUNZIONE: Processa timbratura dopo selezione posizione (admin mode)
  const processTimbratura = useCallback(async (userId, type, userName, locationId = null) => {
    try {
      const typeText = type === 'in' ? 'INGRESSO' : 'USCITA';

      // Mostra notifica di elaborazione
      showNotification(
        `⏳ Elaborazione ${typeText} in corso...`,
        'info',
        'normal'
      );

      // Processa la timbratura
      let result;
      if (type === 'in') {
        // ✅ Passa locationId a clockIn
        result = await timekeepingService.clockIn(userId, locationId, {
          deviceId,
          isAdmin,
          deviceType: kioskMode ? 'kiosk' : 'admin-scanner',
          kioskMode
        });
        
        // ✅ Ottieni nome posizione per il messaggio
        const locationName = locationId ? await getLocationName(locationId) : null;
        
        // Notifica di successo dettagliata per INGRESSO
        let successMessage = `✅ ${result.userName || 'Dipendente'}\n` +
                              `🔵 INGRESSO registrato alle ${result.clockInTime}\n` +
                              `📅 Data: ${result.date}`;
        
        if (locationName) {
          successMessage += `\n📍 Posizione: ${locationName}`;
        }
        
        showNotification(successMessage, 'success', 'large');
        
      } else {
        result = await timekeepingService.clockOut(userId, {
          deviceId,
          isAdmin,
          deviceType: kioskMode ? 'kiosk' : 'admin-scanner',
          kioskMode
        });

        // Notifica di successo dettagliata per USCITA
        let successMessage = `✅ ${result.userData?.nome && result.userData?.cognome ? 
          `${result.userData.nome} ${result.userData.cognome}` : 
          result.userName || 'Dipendente'}\n` +
          `🔴 USCITA registrata alle ${result.clockOutTime}\n` +
          `⏱️ Ore totali: ${result.totalHours}h`;
        
        if (result.standardHours !== undefined && result.overtimeHours !== undefined) {
          successMessage += `\n📊 Standard: ${result.standardHours}h | Straordinario: ${result.overtimeHours}h`;
        }
        
        if (result.isAutoClose) {
          successMessage += `\n⚠️ Turno auto-chiuso per durata eccessiva`;
        }
        
        showNotification(successMessage, 'success', 'large');
      }

      // Reset per nuova scansione
      setScanType('');

    } catch (error) {
      console.error('Errore processamento timbratura:', error);
      
      // Gestisci errori specifici con messaggi chiari
      let errorMessage = '❌ ERRORE TIMBRATURA\n';
      
      if (error.message.includes('già un ingresso attivo')) {
        errorMessage += '🔵 INGRESSO già registrato\n❗ Prima devi timbrare l\'USCITA';
      } else if (error.message.includes('Nessun ingresso attivo')) {
        errorMessage += '🔴 USCITA non possibile\n❗ Prima devi timbrare l\'INGRESSO';
      } else if (error.message.includes('QR code disattivato')) {
        errorMessage += '🚫 QR Code disattivato\n📞 Contatta l\'amministratore';
      } else if (error.message.includes('Utente non trovato')) {
        errorMessage += '👤 Utente non trovato nel sistema\n📞 Contatta l\'amministratore';
      } else if (error.message.includes('non valido')) {
        errorMessage += '📱 QR Code non valido\n🔄 Riprova con un QR Code corretto';
      } else if (error.message.includes('Turno troppo lungo')) {
        errorMessage += '⏰ Turno troppo lungo rilevato\n✅ Auto-chiuso a 8 ore standard';
      } else {
        errorMessage += `📝 ${error.message}\n🔄 Riprova o contatta l\'assistenza`;
      }

      showNotification(errorMessage, 'error', 'large');
      
      // Reset per nuova scansione
      setScanType('');
    }
  }, [deviceId, isAdmin, kioskMode, showNotification]);

  // ✅ NUOVA: Gestisce conferma posizione dal modal (admin mode)
  const handleLocationConfirm = useCallback(async (locationId) => {
    console.log('Posizione selezionata:', locationId);
    
    setShowLocationModal(false);
    
    if (pendingTimbratura) {
      const { userId, type, userName } = pendingTimbratura;
      
      // Processa la timbratura con la posizione selezionata
      await processTimbratura(userId, type, userName, locationId);
      
      setPendingTimbratura(null);
    }
  }, [pendingTimbratura, processTimbratura]);

  // ✅ MODIFICATA: Gestisce la scansione QR
  const handleScan = useCallback(async (qrText, type) => {
    try {
      console.log('QR scansionato:', qrText, 'Tipo:', type);
      
      // Verifica formato QR Code
      const parts = qrText.split(':');
      if (parts.length < 3 || parts[0] !== 'iacuzzo' || parts[1] !== 'user') {
        throw new Error('QR Code non valido - Formato non riconosciuto');
      }

      const userId = parts[2];

      // ✅ LOGICA DIFFERENZIATA: Kiosk vs Admin
      if (kioskMode) {
        // ✅ KIOSK MODE: Recupera posizione automatica dal deviceId
        console.log('Kiosk mode: recupero posizione automatica dal deviceId:', deviceId);
        
        if (type === 'in') {
          // Recupera la posizione assegnata al kiosk
          const kioskLocation = await getKioskDeviceLocation(deviceId);
          console.log('Posizione kiosk recuperata:', kioskLocation);
          
          // Processa con la posizione automatica
          await processTimbratura(userId, type, null, kioskLocation);
        } else {
          // Uscita senza posizione
          await processTimbratura(userId, type, null, null);
        }
        
      } else {
        // ✅ ADMIN MODE: Mostra modal per selezione posizione (solo ingresso)
        if (type === 'in') {
          console.log('Admin mode: ingresso rilevato, mostro modal posizione');
          
          // Salva dati timbratura in sospeso
          setPendingTimbratura({
            userId,
            type,
            userName: null
          });
          
          // Mostra modal
          setShowLocationModal(true);
        } else {
          // Uscita senza posizione
          console.log('Admin mode: uscita rilevata, processa direttamente');
          await processTimbratura(userId, type, null, null);
        }
      }

    } catch (error) {
      console.error('Errore processamento QR:', error);
      
      let errorMessage = '❌ ERRORE SCANSIONE QR\n';
      if (error.message.includes('non valido')) {
        errorMessage += '📱 QR Code non valido\n🔄 Riprova con un QR Code corretto';
      } else {
        errorMessage += `📝 ${error.message}\n🔄 Riprova`;
      }
      
      showNotification(errorMessage, 'error', 'large');
      setScanType('');
    }
  }, [kioskMode, deviceId, processTimbratura, showNotification]);

  // Avvia lo scanner
  const startScanner = useCallback(async (type) => {
    if (!selectedCamera) {
      showNotification(
        '📷 Nessuna camera disponibile\n⚙️ Verifica le impostazioni',
        'error'
      );
      return;
    }

    try {
      console.log(`Avvio scanner ${type}...`);
      
      setIsScanning(true);
      setScanType(type);
      setIsCaptured(false);
      
      const typeText = type === 'in' ? 'INGRESSO' : 'USCITA';
      const modeText = kioskMode ? 'Kiosk' : 'Admin';
      
      showNotification(
        `📸 Scanner ${typeText} ${modeText} attivato\n⏳ Inquadra il QR Code`,
        'info',
        'normal'
      );

      // Inizializzazione asincrona
      setTimeout(async () => {
        try {
          console.log('Timeout inizializzazione completato');
          
          // Crea nuova istanza solo se non esiste
          if (html5QrCode.current === null) {
            console.log('Creazione nuova istanza Html5Qrcode');
            html5QrCode.current = new Html5Qrcode("qr-reader");
          }

          const config = {
            fps: 10,
            qrbox: kioskMode ? { width: 400, height: 400 } : { width: 300, height: 300 },
            aspectRatio: 1.0
          };

          let localCaptured = false;

          // Callback successo scansione
          const onScanSuccess = async (decodedText, decodedResult) => {
            if (localCaptured) {
              console.log('Scansione già processata, skip');
              return;
            }

            localCaptured = true;
            setIsCaptured(true);

            console.log('QR Code rilevato:', decodedText);

            try {
              await stopScanner();
              
              // ✅ Usa la nuova funzione handleScan
              await handleScan(decodedText, type);
              
            } catch (processError) {
              console.error('Errore durante il processamento:', processError);
              localCaptured = false;
              setIsCaptured(false);
              setScanType('');
              setIsScanning(false);
            }
          };
          
          // Callback errore (ignoriamo gli errori di non rilevamento)
          const onScanFailure = (errorMessage) => {
            // Non loggare errori continui di non rilevamento
          };

          console.log('Avvio scanner con camera:', selectedCamera);

          // Avvia scanner
          await html5QrCode.current.start(
            selectedCamera,
            config,
            onScanSuccess,
            onScanFailure
          );

          console.log(`Scanner ${typeText} avviato con successo`);

          // Imposta timeout per chiusura automatica
          timeoutRef.current = setTimeout(async () => {
            console.log('Timeout scanner - nessuna scansione effettuata');
            if (!localCaptured) {
              await stopScanner();
              setScanType('');
              showNotification(
                '⏱️ Timeout scansione raggiunto\n🔄 Seleziona nuovamente il tipo di timbratura',
                'warning',
                'normal'
              );
            }
          }, SCAN_TIMEOUT);

        } catch (initError) {
          console.error('Errore inizializzazione scanner:', initError);
          await stopScanner();
          setScanType('');
          showNotification(
            `🚫 Errore inizializzazione scanner\n📝 ${initError.message}\n🔄 Riprova`,
            'error'
          );
        }
      }, 100);

    } catch (error) {
      console.error('Errore avvio scanner:', error);
      await stopScanner();
      setScanType('');
      showNotification(
        `🚫 Errore avvio scanner\n🔄 Riprova o ricarica la pagina`,
        'error'
      );
    }
  }, [selectedCamera, stopScanner, showNotification, handleScan, kioskMode]);

  // Gestisce la selezione del tipo di scansione
  const handleScanTypeSelection = useCallback((type) => {
    startScanner(type);
  }, [startScanner]);

  // Gestisce cambio camera (solo in modalità admin)
  const handleCameraChange = useCallback((e) => {
    setSelectedCamera(e.target.value);
  }, []);

  // Ferma lo scanner manualmente
  const handleStopScanner = useCallback(async () => {
    await stopScanner();
    setScanType('');
    showNotification(
      '⏹️ Scanner fermato manualmente\n🔄 Seleziona nuovamente il tipo di timbratura',
      'info'
    );
  }, [stopScanner, showNotification]);

  // Render per modalità admin
  if (!kioskMode) {
    return (
      <div className="timekeeping-scanner admin-mode">
        <div className="scanner-header">
          <h2>Scanner QrCode</h2>
        </div>

        {/* Notifiche Migliorate */}
        <Notification
          message={notification.message}
          isVisible={notification.show}
          onClose={closeNotification}
          type={notification.type}
          size={notification.size}
          duration={notification.type === 'success' || notification.type === 'error' ? 8000 : 6000}
          playSound={true}
          enableVibration={true}
        />

        {/* ✅ NUOVO: Modal Selezione Posizione (solo admin mode) */}
        <LocationSelectionModal
          isOpen={showLocationModal}
          onClose={() => {
            setShowLocationModal(false);
            setPendingTimbratura(null);
            setScanType('');
          }}
          onConfirm={handleLocationConfirm}
          userName={pendingTimbratura?.userName || 'Dipendente'}
          scanType="in"
        />

        {/* Selettore Camera */}
        {cameras.length > 0 && !isScanning && (
          <div className="camera-selector">
            <label htmlFor="camera-select">Camera:</label>
            <select
              id="camera-select"
              value={selectedCamera}
              onChange={handleCameraChange}
            >
              {cameras.map((camera) => (
                <option key={camera.id} value={camera.id}>
                  {camera.label || `Camera ${camera.id}`}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Pulsanti Selezione Tipo */}
        {!isScanning && (
          <div className="scan-type-buttons">
            <button
              className="scan-type-btn ingresso"
              onClick={() => handleScanTypeSelection('in')}
              disabled={cameras.length === 0}
            >
              🔵 INGRESSO
            </button>
            <button
              className="scan-type-btn uscita"
              onClick={() => handleScanTypeSelection('out')}
              disabled={cameras.length === 0}
            >
              🔴 USCITA
            </button>
          </div>
        )}

        {/* Container Scanner */}
        {isScanning && (
          <div className="scanner-container">
            <div className="scanner-status">
              <p>Scanner {scanType === 'in' ? 'INGRESSO' : 'USCITA'} attivo</p>
              <button className="stop-scanner-btn" onClick={handleStopScanner}>
                Ferma Scanner
              </button>
            </div>
            <div id="qr-reader"></div>
          </div>
        )}

        {/* Messaggio se nessuna camera */}
        {cameras.length === 0 && (
          <div className="no-camera-message">
            <p>❌ Nessuna camera rilevata</p>
            <p>Verifica i permessi della camera e ricarica la pagina</p>
            <button onClick={initializeCameras}>
              Rileva Camera
            </button>
          </div>
        )}
      </div>
    );
  }

  // Render per modalità kiosk
  return (
    <div className="timekeeping-scanner kiosk-mode">
      <div className="kiosk-header">
        <h1>Sistema Timbrature</h1>
      </div>

      {/* Notifiche Migliorate per Kiosk */}
      <Notification
        message={notification.message}
        isVisible={notification.show}
        onClose={closeNotification}
        type={notification.type}
        size={notification.size}
        duration={notification.type === 'success' || notification.type === 'error' ? 10000 : 7000}
        playSound={true}
        enableVibration={true}
      />

      {/* Pulsanti Selezione Tipo - Sempre visibili in kiosk */}
      {!isScanning && (
        <div className="kiosk-scan-buttons">
          <button
            className="kiosk-scan-btn ingresso"
            onClick={() => handleScanTypeSelection('in')}
            disabled={cameras.length === 0}
          >
            <div className="btn-icon">🔵</div>
            <div className="btn-text">INGRESSO</div>
          </button>
          <button
            className="kiosk-scan-btn uscita"
            onClick={() => handleScanTypeSelection('out')}
            disabled={cameras.length === 0}
          >
            <div className="btn-icon">🔴</div>
            <div className="btn-text">USCITA</div>
          </button>
        </div>
      )}

      {/* Istruzioni quando non si sta scansionando */}
      {!isScanning && cameras.length > 0 && (
        <div className="kiosk-instructions">
          <h2>Seleziona il tipo di timbratura</h2>
          <p>Premi INGRESSO o USCITA per attivare lo scanner</p>
        </div>
      )}

      {/* Container Scanner per Kiosk */}
      {isScanning && (
        <div className="kiosk-scanner-container">
          <div className="scanner-type-indicator">
            <h2>Scanner {scanType === 'in' ? 'INGRESSO' : 'USCITA'} Attivo</h2>
            <p>Inquadra il tuo QR Code personale</p>
          </div>
          <div id="qr-reader"></div>
          <div className="scanner-timeout-info">
            <p>⏱️ Scanner si chiuderà automaticamente tra 30 secondi</p>
          </div>
        </div>
      )}

      {/* Messaggio errore camera in kiosk */}
      {cameras.length === 0 && (
        <div className="kiosk-no-camera">
          <div className="error-icon">📷</div>
          <h2>Camera Non Disponibile</h2>
          <p>Impossibile accedere alla camera del dispositivo</p>
          <button onClick={initializeCameras} className="retry-camera-btn">
            Riprova
          </button>
        </div>
      )}
    </div>
  );
};

export default TimekeepingScanner;